/**
 * RPDM Wave 2-UI · Phase 2 · Delivery Layout.
 *
 * The Phase 2 delivery layout mounts inside the existing
 * `ProjectWorkspaceShell` (sticky header + workspace sidenav). It adds
 * a horizontal sub-navigation for the six W2 delivery capabilities
 * (`overview`, `hierarchy`, `dependencies`, `progress`, `effort`,
 * `analytics`) and renders the currently-selected sub-route below.
 *
 * Only `hierarchy` is active in Phase 2 — every other entry renders as
 * a documented disabled placeholder per the frozen W2-UI Rev.2
 * architectural checkpoint (§ 5.2 · OD-W2UI-25).
 *
 * Scope (Phase 2)
 * ---------------
 *   * READ-ONLY delivery layout + read-only hierarchy tree.
 *   * NO editable workflows, NO create/edit/delete affordances.
 *   * NO progress / dependencies / effort / analytics content.
 */
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import ProjectWorkspaceShell from "../ProjectWorkspaceShell";
import DeliverySubNav from "./DeliverySubNav";
import DeliveryHierarchyPage from "./DeliveryHierarchyPage";
import DeliveryDisabledPage from "./DeliveryDisabledPage";
import { FailClosedPlaceholder } from "../../../../components/rpdm/FailClosedPlaceholder";

export default function Phase2DeliveryLayout() {
    const { id } = useParams();

    return (
        <ProjectWorkspaceShell>
            <div
                data-testid="rpdm-delivery-layout"
                className="flex flex-col gap-4"
            >
                <DeliverySubNav engagementId={id} />
                <div data-testid="rpdm-delivery-body">
                    <Routes>
                        <Route index element={<Navigate to="hierarchy" replace />} />
                        <Route path="hierarchy" element={<DeliveryHierarchyPage engagementId={id} />} />
                        <Route path="overview" element={<DeliveryDisabledPage sectionKey="overview" />} />
                        <Route path="dependencies" element={<DeliveryDisabledPage sectionKey="dependencies" />} />
                        <Route path="progress" element={<DeliveryDisabledPage sectionKey="progress" />} />
                        <Route path="effort" element={<DeliveryDisabledPage sectionKey="effort" />} />
                        <Route path="analytics" element={<DeliveryDisabledPage sectionKey="analytics" />} />
                        <Route path="*" element={
                            <FailClosedPlaceholder
                                title="Delivery view not found"
                                message="The requested delivery sub-route is not part of Phase 2."
                                testId="rpdm-delivery-unknown-route"
                            />
                        } />
                    </Routes>
                </div>
            </div>
        </ProjectWorkspaceShell>
    );
}
